import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import ApplePosterGenericPreview, {
  type PosterGenericInput,
} from './previews/apple/ApplePosterGenericPreview';
import ApplePosterPreview from './previews/apple/ApplePosterPreview';
import {
  POSTER_FACE_ASPECT_RATIO,
  POSTER_FACE_SIDE_RATIO,
  POSTER_FACE_TOP_RATIO,
  POSTER_FACE_WIDTH_RATIO,
  POSTER_PHONE_FRAME_ASPECT,
} from './previews/apple/posterCardStyles';
import {
  EnhancedDesignType,
  TemplateState,
} from '../../../store/types/template';
import { mapApiResponseToTemplateState } from './utils/mapApiResponseToTemplateState';

const POSTER_FACE_WIDTH = 279;
const EVENT_FRAME_WIDTH = POSTER_FACE_WIDTH / POSTER_FACE_WIDTH_RATIO;
const EVENT_FRAME_HEIGHT = EVENT_FRAME_WIDTH * POSTER_PHONE_FRAME_ASPECT;

type CardTemplateData = {
  id?: string;
  uuid?: string;
  templateType?: string;
  fields?: unknown;
  barcode?: unknown;
  images?: unknown;
  appleEnhancedDesign?: {
    type?: EnhancedDesignType | null;
    images?: { artwork?: string | null; secondaryLogo?: string | null };
    artworkBrightness?: 'light' | 'dark' | null;
  };
};

export const hasCardPoster = (previewData: CardTemplateData) => {
  const design = previewData.appleEnhancedDesign;
  return !!design?.type && !!design.images?.artwork;
};

const toGenericInput = ({
  id,
  uuid,
  templateType,
  fields,
  barcode,
  images,
  appleEnhancedDesign,
}: CardTemplateData): PosterGenericInput => {
  const template = mapApiResponseToTemplateState({
    id: uuid ?? id ?? '',
    attributes: { templateType, fields, barcode, images, appleEnhancedDesign },
  } as unknown as Parameters<
    typeof mapApiResponseToTemplateState
  >[0]) as TemplateState;
  const design = template.appleEnhancedDesign;

  return {
    templateType: template.templateType,
    fields: template.fields,
    images: design.images,
    artworkBrightness: design.artworkBrightness,
    featuredActions: design.featuredActions,
    barcode: template.barcode,
    logo: template.images.apple.logo || template.images.shared.logo,
  };
};

const TemplateCardPoster = ({
  previewData,
}: {
  previewData: CardTemplateData;
}) => {
  const {
    id,
    uuid,
    templateType,
    fields,
    barcode,
    images,
    appleEnhancedDesign,
  } = previewData;
  const isEventTicket = appleEnhancedDesign?.type === 'eventTicket';
  const designImages = appleEnhancedDesign?.images;
  const artwork = designImages?.artwork;
  const secondaryLogo = designImages?.secondaryLogo;
  const artworkBrightness = appleEnhancedDesign?.artworkBrightness;
  const genericInput = useMemo(
    () =>
      isEventTicket
        ? null
        : toGenericInput({
            id,
            uuid,
            templateType,
            fields,
            barcode,
            images,
            appleEnhancedDesign,
          }),
    [
      isEventTicket,
      id,
      uuid,
      templateType,
      fields,
      barcode,
      images,
      appleEnhancedDesign,
    ]
  );

  return (
    <Box mx="auto" w={`${POSTER_FACE_WIDTH}px`}>
      {isEventTicket ? (
        <Box
          position="relative"
          aspectRatio={POSTER_FACE_ASPECT_RATIO}
          overflow="hidden"
          pointerEvents="none"
          aria-hidden
        >
          <Box
            position="absolute"
            w={`${EVENT_FRAME_WIDTH}px`}
            h={`${EVENT_FRAME_HEIGHT}px`}
            left={`${-EVENT_FRAME_WIDTH * POSTER_FACE_SIDE_RATIO}px`}
            top={`${-EVENT_FRAME_HEIGHT * POSTER_FACE_TOP_RATIO}px`}
          >
            <ApplePosterPreview
              images={{
                artwork: artwork ?? '',
                secondaryLogo: secondaryLogo ?? '',
              }}
              artworkBrightness={artworkBrightness ?? null}
            />
          </Box>
        </Box>
      ) : (
        genericInput && (
          <ApplePosterGenericPreview input={genericInput} faceOnly />
        )
      )}
    </Box>
  );
};

export default TemplateCardPoster;
